import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';
import { RouterLink } from '@angular/router';
import { finalize, Observable } from 'rxjs';
import moment from 'moment';
import { TrimTooltipDirective } from '../../../../../ui-components/directives/trim-tooltip/trim-tooltip.directive';
import {
  CLOSED_TASK_DEFAULT_COLUMNS,
  CLOSED_TASK_FILTER_MAPPING,
  CLOSED_TASK_FILTER_OPTIONS,
  CLOSED_TASK_INSIGHT_KEYS,
  CLOSED_TASK_INSIGHTS,
  CLOSED_TASK_TABLE_FILTER_TABS,
  CLOSED_TASK_VIEW_DETAILS_ACTION,
} from './closed-task.constants';
import { COLUMN_IDENTIFIERS } from '../../const';
import { isFeatureFlaggedColumnVisible } from '../../../../common/helpers/feature-flagged-table-columns/feature-flagged-table-columns.helper';
import { AvatarComponent } from '../../../../../ui-components/lib/avatar/avatar.component';
import { RelatedRequestsDropdownComponent } from '../../../../../ui-components/lib/related-requests-dropdown/related-requests-dropdown.component';
import { SearchService } from '../../../../../ui-components/lib/search-field/search.service';
import { BadgeComponent } from '../../../../../ui-components/lib/badge/badge.component';
import { TruncatePipe } from '../../../../../ui-components/pipes/truncate/truncate.pipe';
import { FilterOption } from '../../../../../ui-components/lib/filter-dropdown/filter-dropdown.model';
import { FilterMapping } from '../../../../../ui-components/lib/filter-dropdown/filter-mapping.enum';
import { ActionListComponent } from '../../../../common/components/action-list/action-list.component';
import { RequestCreatorComponent } from '../../../../common/components/request-creator/request-creator.component';
import { TenantService } from '../../../../common/services/tenant/tenant.service';
import { TenantPathPipe } from '../../../../common/pipes/tenant-path/tenant-path.pipe';
import { FeatureFlagsService } from '../../../../common/services/feature-flags/feature-flags.service';
import { FEATURE_FLAGS } from '../../../../common/constants/feature-flags.constants';
import { FilterTableColByFeatFlagPipe } from '../../../../common/pipes/filter-table-col-by-featFlag/filter-table-col-by-feat-flag.pipe';
import {
  TableColumns,
  TableFilterTab,
} from '../../../../../ui-components/lib/table/table.model';
import { StepStatusEnum } from '../../../../common/enums/step-status';
import { Insight } from '../../../../common/models/insight.model';
import { ActionList } from '../../../../common/models/action-list.model';
import { DEFAULT_PAGE_SIZE } from '../../../../common/constants/pagination.constants';
import { EMPLOYEE_ROUTES } from '../../../../common/constants/employee.routing.constants';
import { StepStatusPipe } from '../../../../common/pipes/step-status/step-status.pipe';
import { MyOrganizationService } from '../../../../common/services/my-organization/my-organization.service';
import { RequestTypeEnum } from '../../../../common/enums/request-type';
import { ClosedRequest } from '../../../../common/models/closed-request.model';
import {
  ApiResponse,
  Meta,
} from '../../../../common/models/api-response.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FilteredTableComponent } from '../../../../common/components/filtered-table/filtered-table.component';
import { FilteredTablePayload } from '../../../../common/components/filtered-table/filtered-table.model';

@Component({
  selector: 'lib-closed-task',
  imports: [
    DatePipe,
    TranslocoPipe,
    AvatarComponent,
    ActionListComponent,
    TruncatePipe,
    TrimTooltipDirective,
    BadgeComponent,
    StepStatusPipe,
    RequestCreatorComponent,
    RouterLink,
    TenantPathPipe,
    FilteredTableComponent,
    FilterTableColByFeatFlagPipe,
    RelatedRequestsDropdownComponent,
  ],
  templateUrl: './closed-task.component.html',
  styleUrl: './closed-task.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ClosedTaskComponent implements OnInit {
  readonly EMPLOYEE_ROUTES = EMPLOYEE_ROUTES;
  protected readonly COLUMN_IDENTIFIERS = COLUMN_IDENTIFIERS;
  private readonly destroyRef = inject(DestroyRef);
  private readonly transloco = inject(TranslocoService);
  private readonly myOrganizationService = inject(MyOrganizationService);
  private readonly tenantService = inject(TenantService);
  private readonly searchService = inject(SearchService);
  private readonly featureFlagService = inject(FeatureFlagsService);

  protected readonly showRelatedRequestsColumn = signal(
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOWS.parent_and_child_approach
    )
  );

  /*********** input *********/
  ClosedRequestFilter = input.required<number | null>();
  totalItems = input<number>(0);

  /*********** table *********/
  isLoading = signal<boolean>(false);
  dataSource = signal<ClosedRequest[]>([]);
  meta = signal<Meta | undefined>(undefined);
  insights = signal<Insight[]>(CLOSED_TASK_INSIGHTS);

  private readonly filterState = [
    StepStatusEnum.COMPLETED,
    StepStatusEnum.REJECTED,
  ];

  private readonly viewDetailsAction: ActionList = {
    ...CLOSED_TASK_VIEW_DETAILS_ACTION,
    label: this.transloco.translate(CLOSED_TASK_VIEW_DETAILS_ACTION.label),
  };

  columns = computed<TableColumns[]>(() =>
    CLOSED_TASK_DEFAULT_COLUMNS.filter(col =>
      isFeatureFlaggedColumnVisible(col.header, {
        showRelatedRequestsColumn: this.showRelatedRequestsColumn(),
      })
    ).map(column => {
      const mappedColumn: any = {
        ...column,
        header: this.transloco.translate(column.header),
      };
      const routerLinkFn = column.viewIconRouterLink;
      if (routerLinkFn) {
        mappedColumn.viewIconRouterLink = (row: any) => [
          this.tenantService.tenantPath(routerLinkFn(row)[0]),
        ];
      }
      return mappedColumn;
    })
  );

  tableFilterTabs = signal<TableFilterTab[]>(
    CLOSED_TASK_TABLE_FILTER_TABS.map(tab => ({
      ...tab,
      label: this.transloco.translate(tab.label),
    }))
  );

  activeTableFilterTab = computed(
    () => this.payload().tab ?? this.tableFilterTabs()[0]
  );

  filterOptions: FilterOption[] = CLOSED_TASK_FILTER_OPTIONS.map(option => ({
    ...option,
    label: this.transloco.translate(option.label),
    placeholder: option.placeholder
      ? this.transloco.translate(option.placeholder)
      : '',
  }));

  readonly filterMapping = CLOSED_TASK_FILTER_MAPPING;

  payload = signal<FilteredTablePayload>({
    page: 1,
    size: DEFAULT_PAGE_SIZE,
    sortColumn: { key: '', value: 'asc' },
    search: '',
    filters: {},
    tab: this.tableFilterTabs()[0],
  });

  ngOnInit(): void {
    this.getClosedRequestInsights();
  }

  checkReturnedObservable = (): Observable<ApiResponse<ClosedRequest>> => {
    this.isLoading.set(true);
    this.meta.set(undefined);

    const { page, size, sortColumn, search, filters } = this.payload();
    const { from, to } = this.getDateRangeFromTab(
      this.activeTableFilterTab().key
    );
    const hasDateFilter =
      filters[FilterMapping.CREATED_AFTER_REQUESTS] ||
      filters[FilterMapping.CREATED_BEFORE_REQUESTS];

    return this.myOrganizationService
      .getClosedRequestsList(
        page,
        size,
        this.filterState,
        sortColumn,
        this.searchService.checkSearchLength(search),
        hasDateFilter ? null : from,
        hasDateFilter ? null : to,
        filters
      )
      .pipe(finalize(() => this.isLoading.set(false)));
  };

  handleClosedTaskSuccess(res: ApiResponse<ClosedRequest>) {
    this.dataSource.set(
      res.data.map(row => ({
        ...row,
        actionList: this.getViewActionList(row.id),
      }))
    );
    this.meta.set(res.meta);
  }

  handleApiCall() {
    this.checkReturnedObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => this.handleClosedTaskSuccess(res),
      });
  }

  private getDateRangeFromTab(tabKey: string): {
    from: string | null;
    to: string | null;
  } {
    const format = 'yyyy-MM-DD';
    switch (tabKey) {
      case 'all':
        return { from: null, to: null };
      case 'previous':
        return {
          from: moment().subtract(1, 'months').startOf('month').format(format),
          to: moment().subtract(1, 'months').endOf('month').format(format),
        };
      case 'current':
      default:
        return {
          from: moment().startOf('month').format(format),
          to: moment().endOf('month').format(format),
        };
    }
  }

  protected getRelatedRequestViewLink = (childFlow: { id: number }) =>
    this.tenantService.tenantPath(
      `${EMPLOYEE_ROUTES.MY_ORGANIZATION.REQUEST}/${RequestTypeEnum.ACTION}/${childFlow.id}`
    );

  getTaskRouterLink(taskId: number): string[] {
    return [
      this.tenantService.tenantPath(
        `${EMPLOYEE_ROUTES.MY_ORGANIZATION.REQUEST}/${RequestTypeEnum.ACTION}/${taskId.toString()}`
      ),
    ];
  }

  getViewActionList(taskId: number): ActionList[] {
    return [
      {
        ...this.viewDetailsAction,
        routerLinks: [
          this.tenantService.tenantPath(
            `${EMPLOYEE_ROUTES.MY_ORGANIZATION.REQUEST}/${RequestTypeEnum.ACTION}/${taskId.toString()}`
          ),
        ],
      },
    ];
  }

  getClosedRequestInsights() {
    this.myOrganizationService
      .getClosedRequestsInsights()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => {
        this.insights.set([
          {
            ...this.insights()[0],
            value:
              res.data
                .find((insight: Insight) => insight.key === CLOSED_TASK_INSIGHT_KEYS.approvedRequests)
                ?.value?.toString() ?? '0',
          },
          {
            ...this.insights()[1],
            value:
              res.data
                .find((insight: Insight) => insight.key === CLOSED_TASK_INSIGHT_KEYS.rejectedRequests)
                ?.value?.toString() ?? '0',
          },
          {
            ...this.insights()[2],
            value:
              (res.data
                .find(
                  (insight: Insight) =>
                    insight.key === CLOSED_TASK_INSIGHT_KEYS.averageCompletionOnTime
                )
                ?.value?.toString() ?? '0') + '%',
          },
        ]);
      });
  }
}
